import React, { useCallback, useEffect, useRef, useState } from 'react';
import ViolationStateBlock from '../components/ViolationStateBlock';
import ChangeRequestCard from './ChangeRequestCard';
import ChangeRequestDetailModal from './ChangeRequestDetailModal';
import { getChangeRequest, listMyChangeRequests } from '../../../services/analyticsChangeRequests';
import { describeHttpError } from '../../../utils/apiError';
import { formatInTimezone } from '../../../utils/formatInTimezone';
import { APP_EVENTS, useAppEvent } from '../../../utils/appEvents';
import { useCameraNames } from '../../../hooks/useCameraNames';
import { requestDisplayStatus } from '../../../utils/changeRequestStatus';

/**
 * «Мои заявки» (ТЗ 30–32, CR-USER01/02).
 *
 * Не-администратор должен находить поданные заявки после перезагрузки
 * страницы — единственный источник истины GET /change-requests, а не
 * состояние ZoneEditor в памяти вкладки. Рефетч идёт по той же шине, что и
 * badge администратора: сразу после submit заявка обязана появиться в списке
 * (ТЗ 30), а не только после следующего visibilitychange.
 *
 * ZONE-TOMBSTONE01: если зону удалили одобренной заявкой zone_delete, сама
 * зона из «живой» конфигурации пропадает — единственное, что подтверждает,
 * что она вообще существовала, это данные самой заявки
 * (payload.display_name/zone_id, reviewed_at). Сообщение строится только из
 * них и без localStorage; если backend когда-нибудь не пришлёт эти поля,
 * сообщение не подставляет ничего вымышленного (см. отчёт задачи —
 * зафиксирована зависимость от history-контракта на бэкенде).
 */

const readNote = (request) =>
  request.note ?? request.review_note ?? request.admin_note ?? request.reason ?? null;

const zoneLabel = (request) =>
  request.payload?.display_name || request.zone_display_name || request.zone_id || '';

const MyChangeRequestsTab = () => {
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [selectedError, setSelectedError] = useState('');

  // Finding 6: openRequest запускает GET подробностей заявки без гейта на
  // закрытие. Если пользователь закрывает модалку до ответа, поздний resolve
  // раньше молча вызывал setSelected(detail) и модалка «сама» открывалась
  // заново. Ref, а не state — значение нужно синхронно внутри .then/.catch,
  // не через ре-рендер.
  const openRequestIdRef = useRef(null);

  const load = useCallback(() => {
    setPhase((current) => (current === 'ready' ? current : 'loading'));

    return listMyChangeRequests()
      .then((response) => {
        setItems(response.items);
        setPhase('ready');
      })
      .catch((error) => {
        setItems([]);
        setErrorMessage(describeHttpError(error, 'Не удалось загрузить заявки'));
        setPhase('error');
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  // ТЗ 30: заявка появляется в списке сразу после отправки из ZoneEditor —
  // AnalyticsTab.proposeChange emit-ит это же событие.
  useAppEvent(APP_EVENTS.CHANGE_REQUESTS_REFRESH, load);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // ТЗ 29/31, finding 5: список показывает имя камеры, а не только её id —
  // заявка не всегда несёт camera_name сама, общий кэш с ChangeRequestsTab.
  const { cameraLabel: cameraNameById } = useCameraNames(items);
  const cameraLabel = (request) => request.camera_name || cameraNameById(request.camera_id);

  const openRequest = (request) => {
    setSelectedError('');
    setSelected(request);

    const requestId = request.request_id || request.change_request_id;
    openRequestIdRef.current = requestId;
    if (!requestId) return;

    getChangeRequest(requestId)
      .then((detail) => {
        // Модалку успели закрыть (или открыть другую заявку) до ответа —
        // применять его уже некуда.
        if (openRequestIdRef.current !== requestId) return;
        setSelected(detail);
      })
      .catch((error) => {
        if (openRequestIdRef.current !== requestId) return;
        // GET подробностей не обязателен для показа модалки — у нас уже
        // есть карточка из списка, поэтому оставляем её и просто сообщаем
        // об ошибке, а не бросаем пользователя с пустым диалогом.
        setSelectedError(describeHttpError(error, 'Не удалось загрузить подробности заявки'));
      });
  };

  const closeRequest = () => {
    openRequestIdRef.current = null;
    setSelected(null);
    setSelectedError('');
  };

  const renderExtra = (request) => {
    if (request.status === 'rejected') {
      const note = readNote(request);
      return (
        <>
          {/* Дубля «Заявка отклонена» нет: то же самое говорит badge статуса. */}
          {request.reviewed_at && (
            <p className="vio-field-hint">Рассмотрена {formatInTimezone(request.reviewed_at)}</p>
          )}
          <p className="vio-field-hint">
            {note ? `Комментарий администратора «${note}»` : 'Причина не указана'}
          </p>
        </>
      );
    }

    const status = requestDisplayStatus(request);

    // FE-FIX06: зону, созданную по этой заявке, позже удалили — заявка больше
    // не описывает действующую настройку. Дату берём только из zone_deleted_at:
    // reviewed_at — это время рассмотрения, а не удаления.
    if (status.deleted) {
      const name = zoneLabel(request);
      const when = request.zone_deleted_at ? ` ${formatInTimezone(request.zone_deleted_at)}` : '';
      return (
        <p className="vio-notice">
          Зона {name ? `«${name}» ` : ''}удалена{when}. Она больше не участвует в правилах.
        </p>
      );
    }

    // Cheap fix: остальной код (ChangeRequestDetailModal, ChangeRequestCard)
    // читает нормализованное change_type; request_type — только фоллбэк для
    // ответов, которые его ещё не отдают.
    if ((request.change_type || request.request_type) === 'zone_delete' && request.status === 'approved') {
      const name = zoneLabel(request);
      return (
        <p className="vio-notice">
          Зона «{name}» удалена администратором
          {request.reviewed_at ? ` ${formatInTimezone(request.reviewed_at)}` : ''}. Она больше не участвует в правилах.
        </p>
      );
    }

    return null;
  };

  if (phase === 'loading') return <ViolationStateBlock state="loading" />;
  if (phase === 'error') {
    return <ViolationStateBlock state="error" message={errorMessage} onRetry={load} />;
  }
  if (items.length === 0) {
    return (
      <ViolationStateBlock
        state="empty"
        message="Заявок пока нет. Подать заявку можно в «Настройки камер»."
      />
    );
  }

  return (
    <>
      <ul className="vio-cr-list">
        {items.map((request) => (
          <ChangeRequestCard
            key={request.request_id || request.change_request_id}
            request={request}
            cameraLabel={cameraLabel(request)}
            onOpen={openRequest}
            extra={renderExtra(request)}
          />
        ))}
      </ul>

      {selected && (
        <ChangeRequestDetailModal
          request={selected}
          readOnly
          error={selectedError}
          cameraLabel={cameraLabel(selected)}
          onClose={closeRequest}
        />
      )}
    </>
  );
};

export default MyChangeRequestsTab;
